import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import {
  MdWarning, MdChevronRight,
  MdSouthWest, MdNorthEast,
} from 'react-icons/md';
import { useFinanceSummary } from '../../hooks/useFinanceSummary';
import { useCalendar } from '../../context/CalendarContext';
import { useTenant } from '../../context/TenantContext';
import useTheme from '../../context/ThemeContext';
import DualDatePicker from '../../components/common/DualDatePicker';
import UpcomingBills from '../../components/admin/UpcomingBills';
import { adToBs, adMonthToBsRangeLabel, formatAdMonth } from '../../utils/calendar';

const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtPercent = (n) => `${Number(n || 0).toFixed(1)}%`;

// recharts renders inline SVG (CSS vars resolve unreliably on stroke attrs), so use
// literal theme hex tuned for the dark premium look. income/expense match the
// "Money in / Money out" cards.
const CHART = {
  income:    '#34D399',                 // emerald-400
  expense:   '#F87171',                 // red-400
  grid:      'rgba(140,140,140,0.18)',  // divider, low opacity
  axis:      '#8b949e',                 // --color-muted
  tipBg:     '#161b22',                 // --color-card (dark)
  tipBorder: '#30363d',                 // --color-border (dark)
  tipText:   '#E6EDF3',                 // text-secondary (near-white)
};

function SkeletonCard() {
  return (
    <div className="bg-card rounded-2xl border border-divider p-4 shadow-sm">
      <div className="h-4 bg-nav-hover rounded-xl w-24 mb-3 animate-pulse" />
      <div className="h-8 bg-nav-hover rounded-xl w-32 mb-2 animate-pulse" />
      <div className="h-3 bg-nav-hover rounded-xl w-20 animate-pulse" />
    </div>
  );
}

function BreakdownPanel({ title, items, total }) {
  if (items.length === 0) {
    return null;
  }

  const maxAmount = Math.max(...items.map(i => i.amount), 1);

  return (
    <div className="bg-card rounded-2xl border border-divider p-4 shadow-sm">
      <h3 className="text-sm font-bold text-secondary mb-4">{title}</h3>
      <div className="space-y-3">
        {items.slice(0, 5).map((item, idx) => {
          const widthPercent = (item.amount / maxAmount) * 100;
          return (
            <div key={idx}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-medium text-muted truncate flex-1">{item.label}</p>
                <p className="text-xs font-semibold text-secondary flex-shrink-0 ml-2">{fmtNPR(item.amount)}</p>
              </div>
              <div className="w-full h-2 bg-nav-hover rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${widthPercent}%` }}
                />
              </div>
            </div>
          );
        })}
        {items.length > 5 && (
          <p className="text-xs text-muted mt-2">+{items.length - 5} more categories</p>
        )}
      </div>
    </div>
  );
}

function TrendChart({ data, monthLabel }) {
  if (!data || data.length === 0) {
    return null;
  }

  const lastLabel = data[data.length - 1]?.label;

  // Small dots for past months; a larger ringed dot on the LAST point (the selected
  // month) so it reads as the anchor of the series.
  const renderDot = (color) => (props) => {
    const { cx, cy, index } = props;
    if (cx == null || cy == null) return null;
    const isLast = index === data.length - 1;
    return (
      <circle
        key={`d-${index}`}
        cx={cx}
        cy={cy}
        r={isLast ? 5 : 3}
        fill={color}
        stroke={isLast ? CHART.tipBg : 'none'}
        strokeWidth={isLast ? 2 : 0}
      />
    );
  };

  return (
    <div className="bg-card rounded-2xl border border-divider p-4 shadow-sm">
      <div className="mb-4">
        <h3 className="text-sm font-bold text-secondary">Income vs Expenses over time</h3>
        {monthLabel && (
          <p className="text-[13px] text-muted mt-0.5">The 6 months leading up to {monthLabel}.</p>
        )}
        <p className="text-xs text-muted mt-2">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: CHART.income }} />
          Green = money in
          <span className="inline-block w-2 h-2 rounded-full mx-1.5 align-middle" style={{ background: CHART.expense }} />
          Red = money out
        </p>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
          <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: CHART.axis }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: CHART.axis }}
            tickFormatter={(v) => `NPR ${(v / 1000).toFixed(0)}k`}
          />
          {lastLabel != null && (
            <ReferenceLine x={lastLabel} stroke={CHART.grid} strokeDasharray="3 3" />
          )}
          <Tooltip
            formatter={(v) => fmtNPR(v)}
            contentStyle={{ backgroundColor: CHART.tipBg, border: `1px solid ${CHART.tipBorder}`, borderRadius: '8px' }}
            labelStyle={{ color: CHART.tipText }}
          />
          <Line
            type="monotone"
            dataKey="income"
            stroke={CHART.income}
            strokeWidth={2}
            dot={renderDot(CHART.income)}
            activeDot={{ r: 5, fill: CHART.income, stroke: CHART.tipBg, strokeWidth: 2 }}
            name="Income"
          />
          <Line
            type="monotone"
            dataKey="expenses"
            stroke={CHART.expense}
            strokeWidth={2}
            dot={renderDot(CHART.expense)}
            activeDot={{ r: 5, fill: CHART.expense, stroke: CHART.tipBg, strokeWidth: 2 }}
            name="Expenses"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function FinanceOverview({ onNavigateTab, initialMonth }) {
  const { formatMonth } = useCalendar(); // display is unconditional BS now
  const { club } = useTenant();
  // Finance grouping stays on the club's STORED (now-frozen) defaultCalendar — NOT the display
  // calendar — so existing clubs' month numbers don't move (Option A). Admins already saw this
  // exact value before the toggle was retired. Frozen field, scheduled to move to BS separately.
  const financeCalendar = club?.defaultCalendar || 'ad';
  const currentTheme = useTheme()?.currentTheme;

  // Single source of truth: an AD "YYYY-MM" string (DualDatePicker always emits AD,
  // converting from the user's BS selection internally). Init to a drilled-in month
  // (from Reports) if provided, else the current Kathmandu month.
  const [monthStr, setMonthStr] = useState(() => {
    if (initialMonth) return initialMonth;
    const d = new Date();
    const kthOffset = 5 * 60 * 60 * 1000 + 45 * 60 * 1000; // UTC+5:45
    const kthNow = new Date(d.getTime() + kthOffset);
    return `${kthNow.getUTCFullYear()}-${String(kthNow.getUTCMonth() + 1).padStart(2, '0')}`;
  });

  // Apply a new drill-down target when arriving from the Reports tab (manual picks unaffected).
  useEffect(() => {
    if (initialMonth) setMonthStr(initialMonth);
  }, [initialMonth]);

  // Translate the AD monthStr into the month/year/calendar the API expects. In BS mode
  // we convert via day-15 of the AD month (matches DualDatePicker's BS↔AD mapping).
  const [adY, adM] = monthStr.split('-').map(Number);
  let apiMonth = adM, apiYear = adY;
  if (financeCalendar === 'bs') {
    const bs = adToBs(new Date(adY, adM - 1, 15));
    apiMonth = bs.month;
    apiYear = bs.year;
  }

  const { data, isLoading, isError, refetch } = useFinanceSummary(apiMonth, apiYear, financeCalendar);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-56 bg-nav-hover animate-pulse rounded-2xl" />
        <SkeletonCard />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <MdWarning className="mx-auto text-4xl text-amber-500 mb-3" />
          <p className="text-sm font-semibold text-secondary mb-2">Unable to load summary</p>
          <p className="text-xs text-muted mb-4">There was an error fetching your finance data</p>
          <button
            onClick={() => refetch()}
            className={`px-4 py-2 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} rounded-full text-xs font-medium hover:opacity-90 transition-all duration-200`}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const income = data.income?.total || 0;
  const expenses = data.expenses?.total || 0;
  const net = data.net || 0;
  const margin = data.margin || 0;
  const pendingFees = data.pendingFees?.total || 0;

  return (
    <div className="space-y-6">
      {/* Month Selector — shared BS/AD picker; label follows the active calendar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-sm font-bold text-secondary">{formatMonth(monthStr)}</h2>
        <DualDatePicker monthOnly value={monthStr} onChange={setMonthStr} />
      </div>

      {/* Clarify that finance groups by Gregorian months (unlike the BS display elsewhere), with
          the accurate BS span for reference. Only shown while grouping is AD. */}
      {financeCalendar === 'ad' && (
        <p className="text-xs text-muted -mt-3">
          Financial months follow the Gregorian calendar · {formatAdMonth(monthStr)} ≈ {adMonthToBsRangeLabel(monthStr)}
        </p>
      )}

      {/* This month — real income / expenses / net from the finance summary */}
      <div className="bg-card rounded-2xl border border-divider p-5 shadow-sm">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-[15px] font-medium text-secondary">This month</h3>
            <p className="text-[13px] text-muted mt-0.5">{formatMonth(monthStr)}</p>
          </div>
          <span className="text-[11px] text-secondary bg-nav-hover rounded-full px-2 py-0.5">Actual</span>
        </div>
        <p className="text-[13px] text-muted mt-2 mb-4">Your real income and spending for this month.</p>

        {/* Money in / out */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-nav-hover/40 border border-divider rounded-xl p-3.5">
            <div className="flex items-center gap-1.5">
              <MdSouthWest className="text-emerald-400 text-base flex-shrink-0" />
              <p className="text-xs text-muted">Money in</p>
            </div>
            <p className="text-2xl font-medium text-emerald-400 mt-1.5">{fmtNPR(income)}</p>
            <p className="text-[11px] text-subtle mt-1">Collected this month</p>
          </div>
          <div className="bg-nav-hover/40 border border-divider rounded-xl p-3.5">
            <div className="flex items-center gap-1.5">
              <MdNorthEast className="text-red-400 text-base flex-shrink-0" />
              <p className="text-xs text-muted">Money out</p>
            </div>
            <p className="text-2xl font-medium text-red-400 mt-1.5">{fmtNPR(expenses)}</p>
            <p className="text-[11px] text-subtle mt-1">Spent this month</p>
          </div>
        </div>

        {/* Net this month */}
        <div className="bg-nav-hover/40 border border-divider rounded-xl p-4 mt-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-muted">Net this month</p>
            {income > 0 && (
              <p className="text-[11px] text-subtle mt-0.5">Profit margin: {fmtPercent(margin)}</p>
            )}
          </div>
          <p className={`text-[30px] leading-none font-medium ${net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {`${net >= 0 ? '+ ' : '− '}${fmtNPR(Math.abs(net))}`}
          </p>
        </div>
      </div>

      {/* Pending Fees Alert — opens the Receivables tab (falls back to Payments page) */}
      {pendingFees > 0 && (() => {
        const cardCls = 'block w-full text-left bg-amber-50 border border-amber-200 rounded-2xl p-4 hover:shadow-md transition-shadow';
        const inner = (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MdWarning className="text-2xl text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-900">Pending Fees to Collect</p>
                <p className="text-sm text-amber-800 mt-0.5">
                  {fmtNPR(pendingFees)} from {data.pendingFees?.count || 0} students
                </p>
              </div>
            </div>
            <MdChevronRight className="text-amber-600 flex-shrink-0 text-xl" />
          </div>
        );
        return onNavigateTab ? (
          <button type="button" onClick={() => onNavigateTab('receivables')} className={cardCls}>
            {inner}
          </button>
        ) : (
          <Link to="/admin/payments" className={cardCls}>{inner}</Link>
        );
      })()}

      {/* Upcoming Bills — accounts payable with reminders (independent of month selector) */}
      <UpcomingBills />

      {/* Income & Expense Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownPanel
          title="Income Sources"
          items={data.income?.byCategory || []}
          total={income}
        />
        <BreakdownPanel
          title="Top Expenses"
          items={data.expenses?.byCategory || []}
          total={expenses}
        />
      </div>

      {/* 6-Month Trend Chart */}
      {data.trend && data.trend.length > 0 && (
        <TrendChart data={data.trend} monthLabel={formatMonth(monthStr)} />
      )}
    </div>
  );
}
